import {
    Step1ValueModel,
    Step2ValueModel,
    Step3ValueModel,
    Step4ValueModel,
    Step5ValueModel,
} from './types'
import { NumberType } from './Step2/types'
import { Section } from '../shared/Summary/types'
import React from 'react'
import { ISubmitProducerRegistrationBody } from "@apps/Shared/Services/CompanyRegistrations/interfaces";

const parseNumber = (number: string) => {
    const num = parseInt(number)

    if (isNaN(num)) {
        return null
    }

    return num
}

export function getSubmitProducerRegistrationBody(
    values: Step1ValueModel &
        Step2ValueModel &
        Step3ValueModel &
        Step4ValueModel &
        Step5ValueModel
): ISubmitProducerRegistrationBody {
    const supplierTypeCodes: number[] = []
    if (values.selectedSupplierType) {
        const selectedSupplierTypes = values.selectedSupplierType.filter(
            type => type.isType
        )
        selectedSupplierTypes.forEach(function(type) {
            supplierTypeCodes.push(type.code)
        })
    }

    const body: ISubmitProducerRegistrationBody = {
        GDPRConsentText: values.termsText,
        organizationNumber: values.organizationNumber || '',
        vatNumber: values.vatNumber || '',
        optionalConnection: values.optionalConnection,
        CompanyInformation: {
            companyName: values.companyName || '',
            registeredForFTax: !!(values.registeredForFTax === 'true'),
            registeredForVAT: !!(values.registeredForVAT === 'true'),
            streetAddress: values.streetAddress || '',
            zipCode: values.zipCode || '',
            city: values.city || '',
            country: (values.country && values.country.value) || '',
            email: values.email || '',
            articleSupplierTypeCodes: supplierTypeCodes,
        },
        ProjectedSales: {
            metalPackaging: parseNumber(values.metalPackaging || ''),
            plasticPackagingSmall: parseNumber(
                values.plasticPackagingSmall || ''
            ),
            plasticPackagingLarge: parseNumber(
                values.plasticPackagingLarge || ''
            ),
        },
        ContactInformation: {
            oneContact: values.oneContact || false,
            terms: values.terms || false,
        },
    }

    if (
        values.contactInfo &&
        values.contactInfo.agreement &&
        Object.entries(values.contactInfo.agreement).length
    ) {
        body.ContactInformation.agreement =
            (values.contactInfo && values.contactInfo.agreement) || null
    }

    if (
        values.contactInfo &&
        values.contactInfo.signatory &&
        Object.entries(values.contactInfo.signatory).length
    ) {
        body.ContactInformation.signatory =
            (values.contactInfo && values.contactInfo.signatory) || null
    }

    if (
        values.contactInfo &&
        values.contactInfo.productManager &&
        Object.entries(values.contactInfo.productManager).length
    ) {
        body.ContactInformation.productManager =
            (values.contactInfo && values.contactInfo.productManager) || null
    }

    if (
        values.contactInfo &&
        values.contactInfo.salesReporting &&
        Object.entries(values.contactInfo.salesReporting).length
    ) {
        body.ContactInformation.salesReporting =
            (values.contactInfo && values.contactInfo.salesReporting) || null
    }

    if (
        values.contactInfo &&
        values.contactInfo.combined &&
        Object.entries(values.contactInfo.combined).length
    ) {
        body.ContactInformation.combined =
            (values.contactInfo && values.contactInfo.combined) || null
    }

    return body
}

export function getSections(
    t: Function,
    values: Step1ValueModel &
        Step2ValueModel &
        Step3ValueModel &
        Step4ValueModel
): Section[] {
    function formatBooleanString(value: any) {
        return value === 'true'
            ? t('global.yes').toUpperCase()
            : t('global.no').toUpperCase()
    }
    function formatSupplierTypes() {
        const selectedSupplierTypes = values.selectedSupplierType.filter(
            type => type.isType
        )
        return selectedSupplierTypes.map((type, index) => (
            <p key={index} className="supplierTypeRow">
                {type.description}
            </p>
        ))
    }

    const formatPackaging = (value?: string) => {
        if (typeof value === 'undefined' || value === '') {
            return `-`
        }
        return `${value} ${t('global.pce')}`
    }

    return [
        {
            title: t('companyRegistration.stepLabel.step2'),
            subsections: [
                {
                    title: '',
                    items: [
                        {
                            title: t(
                                'companyRegistration.organisationNumberLabel'
                            ),
                            value: values.organizationNumber,
                            hidden: !values.organizationNumber,
                        },
                        {
                            title: t('companyRegistration.vatNumberLabel'),
                            value: values.vatNumber,
                            hidden: !!values.organizationNumber,
                        },
                        {
                            title: t('companyRegistration.companyName'),
                            value: values.companyName,
                        },
                        {
                            title: t('companyRegistration.fTaxLabel'),
                            value: formatBooleanString(
                                values.registeredForFTax
                            ),
                        },
                        {
                            title: t(
                                'companyRegistration.registeredForVatLabel'
                            ),
                            value: formatBooleanString(values.registeredForVAT),
                        },
                        {
                            title: t('global.articleSupplierType'),
                            value: formatSupplierTypes(),
                        },
                    ],
                },
                {
                    title: t('companyRegistration.invoiceAddress'),
                    items: [
                        {
                            title: t('companyRegistration.streetAddress'),
                            value: values.streetAddress,
                        },
                        {
                            title: t('companyRegistration.zipCode'),
                            value: values.zipCode,
                        },
                        {
                            title: t('companyRegistration.city'),
                            value: values.city,
                        },
                        {
                            title: t('global.country'),
                            value: values.country?.label || '',
                            hidden: values.numberType !== NumberType.Vat,
                        },
                        {
                            title: t('companyRegistration.email'),
                            value: values.email,
                        },
                    ],
                },
            ],
        },
        {
            title: t('companyRegistration.stepLabel.step3'),
            subsections: [
                {
                    title: '',
                    items: [
                        {
                            title: t('companyRegistration.metalPackagings'),
                            value: formatPackaging(values.metalPackaging),
                        },
                        {
                            title: t('companyRegistration.plasticPackagingSm'),
                            value: formatPackaging(
                                values.plasticPackagingSmall
                            ),
                        },
                        {
                            title: t('companyRegistration.plasticPackagingLg'),
                            value: formatPackaging(
                                values.plasticPackagingLarge
                            ),
                        },
                    ],
                },
            ],
        },
        {
            title: t('companyRegistration.stepLabel.step4'),
            subsections: Object.keys(values.contactInfo || [])
                .map(contactType => {
                    const items =
                        (values.contactInfo &&
                            values.contactInfo[contactType]) ||
                        []

                    return {
                        title: t(`companyRegistration.${contactType}`),
                        slim: true,
                        items: Object.keys(items).map(item => ({
                            title: t(`global.${item}`),
                            value:
                                (values.contactInfo &&
                                    values.contactInfo[contactType][item]) ||
                                [],
                        })),
                    }
                })
                .filter(subsection => subsection.items.length > 0),
        },
    ]
}